import { Picker as PlayCanvasPicker, Vec3, Vec4 } from 'playcanvas'

const float32 = new Float32Array(1)
const uint8 = new Uint8Array(float32.buffer)
const two = new Vec4(2, 2, 2, 1)
const one = new Vec4(1, 1, 1, 0)
const MAX_PICKER_SIZE = 512

/** Depth picker copied from 3dgs_web's render-page interaction path. */
export class SurfacePicker {
  constructor(app, cameraEntity) {
    this.app = app
    this.cameraEntity = cameraEntity
    this.depthPicker = null
    this.picker = null
  }

  async pick(x, y, width, height, layers = null, maxPickerSize = MAX_PICKER_SIZE, options = null) {
    const sourceWidth = Math.max(1, Math.floor(width))
    const sourceHeight = Math.max(1, Math.floor(height))
    const resolvedMaxPickerSize = Math.max(1, Number(maxPickerSize) || MAX_PICKER_SIZE)
    const scale = Math.min(1, resolvedMaxPickerSize / Math.max(sourceWidth, sourceHeight))
    const targetWidth = Math.max(1, Math.floor(sourceWidth * scale))
    const targetHeight = Math.max(1, Math.floor(sourceHeight * scale))
    const useDepthBuffer = options?.depth === true
    let targetX = Math.max(0, Math.min(targetWidth - 1, Math.floor(x * scale)))
    let targetY = Math.max(0, Math.min(targetHeight - 1, Math.floor(y * scale)))

    if (useDepthBuffer) {
      if (!this.depthPicker) {
        this.depthPicker = new PlayCanvasPicker(this.app, targetWidth, targetHeight, true)
      }
      targetX = Math.max(0, Math.min(targetWidth - 1, x / sourceWidth * targetWidth))
      targetY = Math.max(0, Math.min(targetHeight - 1, y / sourceHeight * targetHeight))
      this.depthPicker.resize(targetWidth, targetHeight)
      const excludedEntities = Array.isArray(options?.excludedEntities)
        ? options.excludedEntities.filter(entity => entity && !entity.destroyed)
        : []
      const enabledStates = excludedEntities.map(entity => entity.enabled)
      try {
        excludedEntities.forEach(entity => { entity.enabled = false })
        this.depthPicker.prepare(
          this.cameraEntity.camera,
          this.app.scene,
          layers || [this.app.scene.layers.getLayerByName('World')]
        )
      } finally {
        excludedEntities.forEach((entity, index) => { entity.enabled = enabledStates[index] })
      }
      const worldPoint = await this.depthPicker.getWorldPointAsync(targetX, targetY)
      return worldPoint && [worldPoint.x, worldPoint.y, worldPoint.z].every(Number.isFinite)
        ? worldPoint
        : null
    }

    targetY = this.app.graphicsDevice.isWebGL2 ? targetHeight - targetY - 1 : targetY

    if (!this.picker) this.picker = new PlayCanvasPicker(this.app, targetWidth, targetHeight)
    this.picker.resize(targetWidth, targetHeight)
    this.picker.prepare(
      this.cameraEntity.camera,
      this.app.scene,
      layers || [this.app.scene.layers.getLayerByName('World')]
    )

    const projectionInverse = this.cameraEntity.camera.projectionMatrix.clone().invert()
    const cameraWorld = this.cameraEntity.getWorldTransform().clone()
    const pixels = await this.picker.renderTarget.colorBuffer.read(targetX, targetY, 1, 1, {
      renderTarget: this.picker.renderTarget,
      immediate: true
    })

    if (!pixels || pixels.length !== 4 || pixels.every(value => value === 0)) return null
    for (let index = 0; index < 4; index += 1) uint8[index] = pixels[index]
    const depth = float32[0]
    if (!Number.isFinite(depth) || depth <= 0 || depth >= 1) return null

    const position = new Vec4(
      (targetX + 0.5) / targetWidth,
      (targetY + 0.5) / targetHeight,
      depth,
      1
    ).mul(two).sub(one)
    if (!this.app.graphicsDevice.isWebGL2) position.y *= -1
    projectionInverse.transformVec4(position, position)
    position.mulScalar(1 / position.w)

    return cameraWorld.transformPoint(new Vec3(position.x, position.y, position.z), new Vec3())
  }

  async pickMany(points, width, height, layers = null, maxPickerSize = MAX_PICKER_SIZE) {
    if (!Array.isArray(points) || !points.length) return []
    const sourceWidth = Math.max(1, Math.floor(width))
    const sourceHeight = Math.max(1, Math.floor(height))
    const resolvedMaxPickerSize = Math.max(1, Number(maxPickerSize) || MAX_PICKER_SIZE)
    const scale = Math.min(1, resolvedMaxPickerSize / Math.max(sourceWidth, sourceHeight))
    const targetWidth = Math.max(1, Math.floor(sourceWidth * scale))
    const targetHeight = Math.max(1, Math.floor(sourceHeight * scale))

    if (!this.picker) this.picker = new PlayCanvasPicker(this.app, targetWidth, targetHeight)
    this.picker.resize(targetWidth, targetHeight)
    this.picker.prepare(
      this.cameraEntity.camera,
      this.app.scene,
      layers || [this.app.scene.layers.getLayerByName('World')]
    )
    const projectionInverse = this.cameraEntity.camera.projectionMatrix.clone().invert()
    const cameraWorld = this.cameraEntity.getWorldTransform().clone()
    const samples = points.map(point => {
      const targetX = Math.max(0, Math.min(targetWidth - 1, Math.floor(point.x * scale)))
      const screenY = Math.max(0, Math.min(targetHeight - 1, Math.floor(point.y * scale)))
      const targetY = this.app.graphicsDevice.isWebGL2 ? targetHeight - screenY - 1 : screenY
      return { targetX, targetY }
    })
    const minX = Math.min(...samples.map(sample => sample.targetX))
    const maxX = Math.max(...samples.map(sample => sample.targetX))
    const minY = Math.min(...samples.map(sample => sample.targetY))
    const maxY = Math.max(...samples.map(sample => sample.targetY))
    const readWidth = maxX - minX + 1
    const readHeight = maxY - minY + 1
    const pixels = await this.picker.renderTarget.colorBuffer.read(minX, minY, readWidth, readHeight, {
      renderTarget: this.picker.renderTarget,
      immediate: true
    })

    if (!pixels || pixels.length < readWidth * readHeight * 4) return samples.map(() => null)
    return samples.map(({ targetX, targetY }) => {
      const pixelOffset = ((targetY - minY) * readWidth + targetX - minX) * 4
      if (
        pixels[pixelOffset] === 0 &&
        pixels[pixelOffset + 1] === 0 &&
        pixels[pixelOffset + 2] === 0 &&
        pixels[pixelOffset + 3] === 0
      ) return null
      for (let index = 0; index < 4; index += 1) uint8[index] = pixels[pixelOffset + index]
      const depth = float32[0]
      if (!Number.isFinite(depth) || depth <= 0 || depth >= 1) return null
      const position = new Vec4(
        (targetX + 0.5) / targetWidth,
        (targetY + 0.5) / targetHeight,
        depth,
        1
      ).mul(two).sub(one)
      if (!this.app.graphicsDevice.isWebGL2) position.y *= -1
      projectionInverse.transformVec4(position, position)
      position.mulScalar(1 / position.w)
      return cameraWorld.transformPoint(new Vec3(position.x, position.y, position.z), new Vec3())
    })
  }

  destroy() {
    this.depthPicker?.destroy?.()
    this.depthPicker = null
    this.picker?.destroy?.()
    this.picker = null
  }
}
